import { Badge, Button, Card } from "@nfp-tracker/components";
import {
  computeFertileWindow,
  generatePDF,
  getCycleTiming,
  type NFPMethod,
  getDateKeyInTimeZone,
  usesSymptoThermal,
} from "@nfp-tracker/utils";
import { addDays, format, parseISO } from "date-fns";
import { Calendar, FileText, History, TrendingUp } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { EmptyState } from "../components/EmptyState";
import { SubscriptionGate } from "../components/SubscriptionGate";
import { useToast } from "@nfp-tracker/components";
import { ROUTES } from "../constants/routes";
import { useObservationsCache } from "../hooks/useObservationsCache";
import { useOwnerContext } from "../hooks/useOwnerContext";
import { captureException, trackEvent } from "../integrations/analytics/client";
import { fetchAllObservations } from "../utils/observationsCsvIo";
import { deriveCyclesFromObservations, type DerivedObservation } from "../utils/derivedCycles";

type Cycle = {
  id: string;
  start_date: string;
  end_date: string | null;
  first_peak_cd: number | null;
  predicted_avoid_start_cd: number | null;
  post_peak_progress: string;
  /** Logged before the first recorded cycle start — its real start is unknown. */
  start_inferred: boolean;
};

const CycleHistory = () => {
  const [fullCycles, setFullCycles] = useState<Cycle[] | null>(null);
  const [fullObservations, setFullObservations] = useState<DerivedObservation[] | null>(null);
  const [loadingFullHistory, setLoadingFullHistory] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  // Shared, session-cached auth/owner/permission context — repeat navigation between pages
  // no longer re-runs the auth+onboarding+owner-resolution chain from scratch each time.
  const ownerContextQuery = useOwnerContext();
  const ownerContext = ownerContextQuery.data;
  const couple = ownerContext?.status === "ready" ? ownerContext.couple : null;
  const coupleId = couple?.id;
  const userName = couple ? `${couple.first_name || ""} ${couple.last_name || ""}`.trim() : "";
  const nfpMethods = couple?.methods?.length ? couple.methods : ["Marquette"];
  // Reused as the History tab of an instructor's client chart, where a "Start Tracking" CTA
  // would send the instructor to their own entry page instead of doing anything for the client.
  const isClientView =
    ownerContext?.status === "ready" && Boolean(ownerContext.viewingClientUserId);

  // Same ~13-month/~12-cycle shared cache the other pages already warm — this list
  // renders from it immediately instead of an unbounded fetch. Older history beyond
  // that window is loaded only if the user explicitly asks for it, below.
  const cache = useObservationsCache(coupleId, couple?.methods);
  const cycles = fullCycles ?? (cache.cycles as Cycle[]);
  const cycleObservations = fullObservations ?? cache.observations;
  const hasFullHistory = fullCycles !== null;
  const loading = ownerContextQuery.isLoading || cache.isLoading;

  useEffect(() => {
    if (ownerContext?.status === "unauthenticated") {
      navigate(ROUTES.auth);
    } else if (ownerContext?.status === "needs-onboarding") {
      navigate(ROUTES.onboarding);
    }
  }, [ownerContext, navigate]);

  useEffect(() => {
    if (ownerContextQuery.error) {
      console.error("Error loading cycles:", ownerContextQuery.error);
      captureException(ownerContextQuery.error, { source: "cycle_history_owner_context" });
    }
  }, [ownerContextQuery.error]);

  const loadFullHistory = async () => {
    if (!coupleId) return;

    setLoadingFullHistory(true);
    try {
      // Paged: a single request is capped at 1000 rows (~2.7 years), which would silently
      // drop the most recent history from an oldest-first query.
      const observationRows = await fetchAllObservations(coupleId);

      const { cycles: derivedCycles, observations: derivedObservations } =
        deriveCyclesFromObservations(observationRows, nfpMethods);
      setFullCycles(derivedCycles as Cycle[]);
      setFullObservations(derivedObservations);
      trackEvent("cycle_history_full_load_succeeded", { cycleCount: derivedCycles.length });
    } catch (error) {
      console.error("Error loading full cycle history:", error);
      trackEvent("cycle_history_full_load_failed", {});
      captureException(error, { source: "cycle_history_full_load" });
      toast({
        title: "Error",
        description: "Couldn't load your complete cycle history.",
        variant: "destructive",
      });
    } finally {
      setLoadingFullHistory(false);
    }
  };

  // Partial cycles (start never recorded) have no measurable length and are left out of the
  // numbering and the summary figures below.
  // "Completed" means ended before today: pre-logged future cycle starts give the current
  // cycle an end_date it hasn't reached, and upcoming cycles haven't happened yet.
  const todayKey = getDateKeyInTimeZone(new Date(), couple?.timezone);
  const recordedCycles = cycles.filter(
    (c) => !c.start_inferred && getCycleTiming(c, todayKey) !== "upcoming"
  );
  const completedCycles = recordedCycles.filter(
    (c) => getCycleTiming(c, todayKey) === "past"
  );

  const calculateCycleLength = (cycle: Cycle) => {
    if (!cycle.end_date || cycle.start_inferred) return null;
    // The running cycle's end_date (if any) comes from a future start — not a length yet.
    if (getCycleTiming(cycle, todayKey) === "current") return null;
    const start = new Date(cycle.start_date);
    const end = new Date(cycle.end_date);
    return (
      Math.floor((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1
    );
  };

  const handleExportPDF = async () => {
    try {
      if (!coupleId) return;

      // Export always covers complete history, regardless of whether the on-screen
      // list is still the windowed cache — reuse the full fetch if the user already
      // hit "Load complete history" this visit, otherwise fetch it fresh here.
      let exportCycles = fullCycles;
      let exportObservations = fullObservations;

      if (!exportCycles || !exportObservations) {
        const allObservations = await fetchAllObservations(coupleId);

        const derived = deriveCyclesFromObservations(allObservations, nfpMethods);
        exportCycles = derived.cycles as Cycle[];
        exportObservations = derived.observations;
      }

      generatePDF(exportObservations, exportCycles, userName, nfpMethods, couple?.timezone);

      toast({
        title: "Export Complete",
        description: "Your PDF report has been generated.",
      });
    } catch (error: unknown) {
      toast({
        title: "Export Error",
        description: (error as Error).message,
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-full flex items-start justify-center p-4 pt-6 pb-8 sm:items-center">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <SubscriptionGate
      coupleId={coupleId}
      readOnlyMessage="Subscribe to access your complete cycle history and trends."
    >
      <div className="space-y-4 pb-8">
        <div className="flex items-center justify-between gap-2">
          {!hasFullHistory && cycles.length > 0 ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => void loadFullHistory()}
              disabled={loadingFullHistory}
            >
              <History className="mr-2 h-4 w-4" />
              {loadingFullHistory ? "Loading complete history..." : "Load complete history"}
            </Button>
          ) : (
            <span />
          )}
          {cycles.length > 0 && (
            <Button variant="outline" size="sm" onClick={handleExportPDF}>
              <FileText className="mr-2 h-4 w-4" />
              Export PDF
            </Button>
          )}
        </div>

        {cycles.length === 0 ? (
          <EmptyState
            icon={Calendar}
            title="No Cycles Yet"
            description={
              isClientView
                ? "This client has no recorded cycles yet."
                : "Start tracking your observations to build your cycle history. Your first cycle will appear here once you begin."
            }
            actionLabel={isClientView ? undefined : "Start Tracking"}
            onAction={isClientView ? undefined : () => navigate(ROUTES.observations)}
          />
        ) : (
          <div className="animate-fade-in space-y-3">
            {cycles.map((cycle, index) => {
              const length = calculateCycleLength(cycle);
              const timing = getCycleTiming(cycle, todayKey);
              const isActive = timing === "current";
              const isUpcoming = timing === "upcoming";
              const isPartial = cycle.start_inferred;
              const cycleNumber = cycles.slice(index).filter((c) => !c.start_inferred).length;
              // A partial cycle's day numbers are offsets from its first entry, so its Peak is
              // shown as a date rather than a cycle day.
              // The method's own close (Sympto-Thermal double check, a restarted mucus count),
              // not a flat Peak + 3.
              const windowEndCd =
                cycle.first_peak_cd && !isPartial
                  ? computeFertileWindow(
                      cycleObservations.filter((o) => o.cycle_id === cycle.id),
                      nfpMethods as NFPMethod[],
                      { peakCd: cycle.first_peak_cd, isPostpartum: ownerContext?.status === "ready" && ownerContext.isPostpartum }
                    ).endCd
                  : null;
              const peakDateLabel =
                isPartial && cycle.first_peak_cd
                  ? format(
                      addDays(parseISO(cycle.start_date), cycle.first_peak_cd - 1),
                      "MMM d"
                    )
                  : null;

              return (
                <Card
                  key={cycle.id}
                  className="p-5 transition-shadow hover:shadow-md"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="mb-2 flex items-center gap-2">
                        <h3 className="font-semibold">
                          {isPartial
                            ? hasFullHistory
                              ? "Partial cycle — start not recorded"
                              : "Partial cycle — start not in loaded history"
                            : `Cycle ${cycleNumber}`}
                        </h3>
                        {isActive && <Badge variant="default">Current</Badge>}
                        {isUpcoming && <Badge variant="outline">Upcoming</Badge>}
                      </div>

                        <div className="space-y-1 text-sm text-muted-foreground">
                          <div className="flex items-center gap-2">
                            <Calendar className="h-3 w-3" />
                            <span>
                              {isPartial ? "First entry: " : "Started: "}
                              {parseISO(cycle.start_date).toLocaleDateString()}
                            </span>
                          </div>

                          {cycle.end_date && (
                            <div className="flex items-center gap-2">
                              <Calendar className="h-3 w-3" />
                              <span>
                                Ended:{" "}
                                {parseISO(cycle.end_date).toLocaleDateString()}
                              </span>
                            </div>
                          )}

                          {length && (
                            <div className="flex items-center gap-2">
                              <TrendingUp className="h-3 w-3" />
                              <span>Length: {length} days</span>
                            </div>
                          )}

                          {isPartial && (
                            <p className="text-xs">
                              {hasFullHistory
                                ? "Entries logged before your first marked cycle start."
                                : "Its cycle start may be in older history — load complete history to see it."}{" "}
                              Not included in cycle averages.
                            </p>
                          )}

                          {peakDateLabel && (
                            <div className="text-xs">
                              <Badge variant="secondary" className="mt-1">
                                Peak on {peakDateLabel}
                              </Badge>
                            </div>
                          )}

                          {cycle.first_peak_cd && !isPartial && (
                            <div className="text-xs">
                              <Badge variant="secondary" className="mt-1">
                                Peak on CD{cycle.first_peak_cd}
                              </Badge>
                              <p className="mt-1 text-muted-foreground">
                                {windowEndCd !== null
                                  ? `Fertile through CD${windowEndCd} — mucus after that isn't fertile-interpreted`
                                  : usesSymptoThermal(nfpMethods)
                                    ? "Fertile window open — waiting for the temperature shift to confirm"
                                    : "Fertile window open — waiting for the mucus change to confirm"}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="text-right">
                        {length && (
                          <div className="text-2xl font-bold text-primary">
                            {length}
                          </div>
                        )}
                        {isActive && (
                          <div className="text-xs text-muted-foreground mt-1">
                            In progress
                          </div>
                        )}
                      </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}

        {cycles.length > 0 && (
          <Card className="bg-muted p-4">
            <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <div className="text-2xl font-bold">
                    {completedCycles.length}
                  </div>
                  <div className="text-xs text-muted-foreground">Completed</div>
                </div>
                <div>
                  <div className="text-2xl font-bold">
                    {Math.round(completedCycles
                      .reduce(
                        (sum, c) => sum + (calculateCycleLength(c) || 0),
                        0
                      ) / completedCycles.length) || 0}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Avg Length
                  </div>
                </div>
                <div>
                  <div className="text-2xl font-bold">
                    {recordedCycles.filter((c) => c.first_peak_cd).length}
                  </div>
                  <div className="text-xs text-muted-foreground">With Peak</div>
                </div>
            </div>
          </Card>
        )}
      </div>
    </SubscriptionGate>
  );
};

export default CycleHistory;
